import type { DeclarationDApp } from '@taki/ui';
import { patchOptionsChamp, TONS_OPTIONS, type OptionDeChamp, type PatchOptionsChamp } from '../champsEdition.js';
import { appliquerAteliers, projeterAteliers } from './atelier-adapter.js';

export type CibleOptionsChamp = { family: string; type?: string; field: string };

export function lireOptionsChamp(declaration: DeclarationDApp, cible: CibleOptionsChamp) {
  if (cible.field.startsWith('socle:')) {
    const jeux = declaration.champs.jeux.filter(jeu => jeu.family === cible.family && (cible.type === undefined || jeu.type === cible.type));
    if (jeux.length !== 1) throw new Error('Le jeu du catalogue natif est absent ou ambigu. Précisez type.');
    const jeu = jeux[0]!;
    const cibleExacte = { family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }), field: cible.field };
    const colonnes = declaration.ecrans.ecrans.filter(ecran => ecran.portes.some(porte => porte.jeu?.family === jeu.family && porte.jeu.type === jeu.type))
      .flatMap(ecran => ecran.colonnes ?? []).filter(colonne => colonne.champ === cible.field);
    const colonne = colonnes[0];
    if (!colonne?.options?.length || (colonne.type !== 'select' && colonne.type !== 'multiselect')) throw new Error('Le champ natif ne déclare pas de catalogue de choix.');
    if (colonnes.some(c => JSON.stringify(c.options) !== JSON.stringify(colonne.options))) throw new Error('Catalogues natifs différents : préciser une app et un jeu cohérents.');
    return { ...cibleExacte, label: colonne.label ?? cible.field, native: true as const, multiple: colonne.type === 'multiselect', options: structuredClone(colonne.options) as OptionDeChamp[], tones: [...TONS_OPTIONS] };
  }
  const jeux = declaration.champs.jeux.filter((jeu) => jeu.family === cible.family
    && (cible.type === undefined || (jeu.type ?? '') === cible.type));
  if (jeux.length !== 1) throw new Error(`Le jeu « ${cible.family} » est absent ou ambigu. Précisez type depuis studio_read_app.`);
  const jeu = jeux[0]!;
  const field = cible.field.replace(/^customFields\./, '');
  const champ = jeu.champs.find((candidate) => candidate.key === field);
  if (champ === undefined) throw new Error(`Champ inconnu « ${field} ». Champs valides : ${jeu.champs.map((c) => c.key).join(', ')}. Pour un natif, utiliser socle:<nom>.`);
  if (champ.type !== 'select' && champ.type !== 'multiselect') throw new Error(`Le champ « ${field} » n’est pas un champ à choix (select ou multiselect). Utilisez studio_propose_field pour créer un champ.`);
  /* UN CHAMP DEPENDANT (`dependDe`) DIT SON PARENT, et les valeurs de celui-ci :
     c'est ce que « Étiquettes et listes » montre a cote de chaque valeur. */
  const parent = champ.dependDe === undefined ? undefined : jeu.champs.find((candidate) => candidate.key === champ.dependDe);
  return {
    family: jeu.family, ...(jeu.type === undefined ? {} : { type: jeu.type }), field,
    label: champ.label, native: false as const, multiple: champ.type === 'multiselect',
    options: structuredClone(champ.options ?? []) as OptionDeChamp[], tones: [...TONS_OPTIONS],
    ...(parent === undefined ? {} : { dependsOn: { field: parent.key, label: parent.label, options: (parent.options ?? []).map((o) => ({ value: o.value, label: o.label })) } }),
  };
}

/** La proposition applique le même brouillon que l'écran Studio d'instance. */
export function proposerOptionsChamp(declaration: DeclarationDApp, cible: CibleOptionsChamp, patch: PatchOptionsChamp) {
  const champ = lireOptionsChamp(declaration, cible);
  const before = champ.options;
  const after = patchOptionsChamp(before, patch);
  if (JSON.stringify(before) === JSON.stringify(after) && !patch.replace?.length && !patch.clear?.length) throw new Error('La proposition ne change aucune option.');
  if (champ.native) {
    const suivante = structuredClone(declaration);
    for (const ecran of suivante.ecrans.ecrans) {
      if (!ecran.portes.some(porte => porte.jeu?.family === champ.family && (champ.type === undefined || porte.jeu.type === champ.type))) continue;
      for (const colonne of ecran.colonnes ?? []) if (colonne.champ === champ.field) (colonne as { options?: OptionDeChamp[] }).options = after;
    }
    return { declaration: suivante, champ, before, after, renamed: before.filter(o => after.some(n => n.value === o.value && n.label !== o.label)).map(o => o.value), removed: [] };
  }
  const drafts = projeterAteliers(declaration).map((avant, index) => {
    const apres = structuredClone(avant);
    const jeu = apres.jeux.find((candidate) => candidate.famille === champ.family && (candidate.type ?? '') === (champ.type ?? ''));
    const cibleChamp = jeu?.champs.find((candidate) => candidate.clef === champ.field && !(candidate as { natif?: string }).natif);
    if (cibleChamp === undefined) throw new Error(`Le Studio d’instance ne peut pas projeter le champ « ${champ.field} ».`);
    const labels = after.map((option) => option.label);
    const structure = cibleChamp as typeof cibleChamp & { valeursListe: string[]; clefsValeurs: Record<string, string>; tonsValeurs: Record<string, string> };
    (structure as typeof structure & { options: OptionDeChamp[] }).options = after;
    structure.valeursListe = labels;
    structure.valeurs = labels.join(', ');
    structure.clefsValeurs = Object.fromEntries(after.map((option) => [option.label, option.value]));
    structure.tonsValeurs = Object.fromEntries(after.filter((option) => option.tone !== undefined).map((option) => [option.label, option.tone!]));
    (structure as typeof structure & { parentsValeurs?: Record<string, string> }).parentsValeurs =
      Object.fromEntries(after.filter((option) => option.parent !== undefined).map((option) => [option.label, option.parent!]));
    return { index, avant, apres };
  });
  if (drafts.length === 0) throw new Error('La déclaration ne contient aucun écran éditable par le Studio d’instance.');
  const suivante = appliquerAteliers(declaration, drafts);
  const resultat = lireOptionsChamp(suivante, champ);
  if (JSON.stringify(resultat.options) !== JSON.stringify(after)) throw new Error('Le Studio d’instance ne conserve pas cette modification d’options.');
  return {
    declaration: suivante, champ, before, after,
    renamed: before.filter((option) => after.some((o) => o.value === option.value && o.label !== option.label)).map((o) => o.value),
    removed: before.filter((option) => !after.some((o) => o.value === option.value)).map((o) => o.value),
  };
}

/** Ce que la simulation d'options rend, en ce qu'Enregistrer en lit. */
export type SimulationDOptions = {
  migrated?: number;
  counts?: readonly { value: string; count: number; label?: string }[];
};

/**
 * ENREGISTRER UNE LISTE : FAUT-IL CONFIRMER ? `null` quand aucune fiche n'est
 * touchée (ajout, renommage, ordre, couleur, valeur retirée que rien
 * n'utilise) : on enregistre aussitot. Sinon la ligne a confirmer, un compte :
 * les fiches a reecrire, ou celles qui utilisent une valeur retiree.
 */
export function fichesAConfirmer(simulation: SimulationDOptions, patch: PatchOptionsChamp): string | null {
  const aChanger = Number(simulation.migrated ?? 0);
  if (aChanger > 0) return `${aChanger} ${aChanger > 1 ? 'fiches' : 'fiche'} à changer`;
  const retirees = new Set([
    ...(patch.archive ?? []), ...(patch.remove ?? []), ...(patch.clear ?? []), ...(patch.replace ?? []).map((item) => item.from),
  ]);
  const utilisees = (simulation.counts ?? []).filter((item) => retirees.has(item.value) && item.count > 0);
  if (utilisees.length === 0) return null;
  const total = utilisees.reduce((somme, item) => somme + item.count, 0);
  return `${total} ${total > 1 ? 'fiches utilisent' : 'fiche utilise'} ${utilisees.map((item) => item.label ?? item.value).join(', ')}`;
}
